import React, { useEffect, useState } from 'react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  eyebrow,
  focusRing,
  keyQuestionCard,
  keyQuestionRowGrid,
  listItemText,
  meta,
  mono,
  pillAction,
  pressable,
  removeControl,
} from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { stepPosition } from '../advisoryStepPosition';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import type { AdvisoryStage } from '../../../redux/slices/advisory/advisoryTypes';

/**
 * AdvisoryCounterpartiesScreen
 *
 * The OTHER companies an engagement is carried out against — the competitors to
 * be analysed, or the acquisition targets being evaluated.
 *
 * Reachable on exactly two of the twenty-one engagement types, because only
 * those two are per-counterparty: a `competitor-analysis` answers "right to
 * win" against someone, an `investment-m-and-a-decision` answers "valuation
 * synergies" with something. On the other nineteen this screen never renders,
 * and asking for a competitor there would be a question with no right answer.
 *
 * Structurally the key-question editor's smaller sibling — same card, same row
 * grid, same remove control, same add field — because it is the same kind of
 * task: a list the user curates before the set is locked. What it is NOT is a
 * per-row round trip. The list is two to six names edited in one screen, so it
 * is held locally and sent whole on confirm; one array write cannot half-apply
 * the way a delete plus an add can.
 *
 * Receives an already-narrowed stage and never touches the slice or the
 * supervisor hook — `onConfirm` is minted by the caller that owns the write
 * path, the same contract every other advisory screen follows.
 */
export interface AdvisoryCounterpartiesScreenProps {
  stage: Extract<AdvisoryStage, { tag: 'awaitingCounterpartyConfirmation' }>;
  /** True while the confirm turn is in flight; disables every control. */
  busy?: boolean;
  /** The whole curated list, in the user's own order. */
  onConfirm: (companies: string[]) => void;
}

export function AdvisoryCounterpartiesScreen({
  stage,
  busy = false,
  onConfirm,
}: AdvisoryCounterpartiesScreenProps) {
  const { t } = useUntypedTranslation();

  const [companies, setCompanies] = useState<string[]>(stage.companies);
  const [draft, setDraft] = useState('');

  // The extraction arrives over the stream, so the list can land after this
  // screen has mounted. Syncing on the proposal's own identity — not on every
  // render — is what lets it fill in without discarding names the user has
  // since typed.
  //
  // Keyed on the SERIALISED list, never on `stage.companies` itself: the stage
  // object is rebuilt on every render, so an array dependency re-runs this
  // effect forever and throws away each edit as it is made. Serialised rather
  // than joined on a separator, because "Under Armour" is one company and any
  // separator is a character some name could contain.
  const proposed = JSON.stringify(stage.companies);
  useEffect(() => {
    setCompanies(JSON.parse(proposed) as string[]);
  }, [proposed]);

  // `kind` is the server's word for what these are — `competitor` or
  // `acquisition target` — so the copy names them correctly rather than calling
  // an acquisition target a competitor.
  const kindKey = stage.kind === 'acquisition target' ? 'target' : 'competitor';

  const position = stepPosition('goal', null);

  const trimmedDraft = normalizeControlCharacters(draft).trim();
  const alreadyListed = companies.some(name => name.toLowerCase() === trimmedDraft.toLowerCase());
  const canAdd = trimmedDraft.length > 0 && !alreadyListed && !busy;

  const handleAdd = () => {
    if (!canAdd) {
      return;
    }
    setCompanies(current => [...current, trimmedDraft]);
    setDraft('');
  };

  const handleRemove = (index: number) => {
    if (busy) {
      return;
    }
    setCompanies(current => current.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (companies.length === 0 || busy) {
      return;
    }
    onConfirm(companies);
  };

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey={`advisory:counterparties.${kindKey}.eyebrow`}
      titleKey={`advisory:counterparties.${kindKey}.title`}
      hintKey={`advisory:counterparties.${kindKey}.hint`}
      actions={
        <Button
          variant="contained"
          disableRipple
          disabled={companies.length === 0 || busy}
          onClick={handleConfirm}
          data-testid="advisory-counterparties-confirm"
          sx={[pillAction, pressable, focusRing]}
        >
          {t(`advisory:counterparties.${kindKey}.confirmAction`)}
        </Button>
      }
    >
      <Typography
        data-testid="advisory-counterparties-count"
        sx={[eyebrow, theme => ({ marginTop: theme.visualPrimitives.clHeaderGap })]}
      >
        {t(`advisory:counterparties.${kindKey}.countLabel`)}
        {' · '}
        <Box component="span" sx={[mono]}>
          {companies.length}
        </Box>
      </Typography>

      {stage.extractionFailed ? (
        <Typography
          data-testid="advisory-counterparties-failed"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t(`advisory:counterparties.${kindKey}.failedBody`)}
        </Typography>
      ) : null}

      <Box
        data-testid="advisory-counterparties-list"
        sx={theme => ({ marginTop: theme.visualPrimitives.kqListGap })}
      >
        {companies.length === 0 ? (
          <Typography data-testid="advisory-counterparties-empty" sx={[meta]}>
            {t(`advisory:counterparties.${kindKey}.emptyBody`)}
          </Typography>
        ) : (
          companies.map((name, index) => (
            <Box
              key={`${name}-${index}`}
              data-testid="advisory-counterparty-card"
              sx={[keyQuestionCard]}
            >
              <Box sx={[keyQuestionRowGrid]}>
                <Typography
                  component="span"
                  sx={[
                    mono,
                    theme => ({
                      fontSize: theme.visualPrimitives.kqIndexSize,
                      color: theme.colors.lavenderDeep,
                    }),
                  ]}
                >
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={[listItemText]}>{name}</Typography>
                <Button
                  variant="text"
                  disableRipple
                  disabled={busy}
                  onClick={() => handleRemove(index)}
                  data-testid="advisory-counterparty-remove"
                  aria-label={t(`advisory:counterparties.${kindKey}.removeAriaLabel`, {
                    company: name,
                  })}
                  sx={[removeControl, pressable, focusRing]}
                >
                  {t('advisory:counterparties.removeAction')}
                </Button>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Box>
        <InputBase
          fullWidth
          // On `inputProps` so it lands on the inner <input>: MUI puts
          // everything else on the wrapping div, and a test that queries the
          // wrapper cannot read `value`.
          inputProps={{
            'aria-label': t(`advisory:counterparties.${kindKey}.addLabel`),
            'data-testid': 'advisory-counterparties-add-field',
          }}
          placeholder={t(`advisory:counterparties.${kindKey}.addPlaceholder`)}
          value={draft}
          disabled={busy}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleAdd();
            }
          }}
          sx={theme => ({
            fontSize: theme.visualPrimitives.inputSize,
            lineHeight: theme.visualPrimitives.inputLineHeight,
            marginTop: theme.visualPrimitives.inputGap,
            color: theme.colors.inkPrimary,
            borderBottom: `1px solid ${theme.visualPrimitives.border}`,
            '& .MuiInputBase-input': {
              padding: theme.visualPrimitives.inputPaddingY,
            },
            '& .MuiInputBase-input::placeholder': {
              color: theme.colors.inkTertiary,
              opacity: 1,
            },
            '&:focus-within': {
              borderBottomColor: theme.colors.lavenderDeep,
            },
          })}
        />
        <Button
          variant="text"
          disableRipple
          disabled={!canAdd}
          onClick={handleAdd}
          data-testid="advisory-counterparties-add"
          sx={[pressable, focusRing, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t('advisory:counterparties.addAction')}
        </Button>
        {alreadyListed && trimmedDraft ? (
          <Typography data-testid="advisory-counterparties-duplicate" sx={[meta]}>
            {t('advisory:counterparties.duplicateNotice', { company: trimmedDraft })}
          </Typography>
        ) : null}
      </Box>
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryCounterpartiesScreen;
